import type { ConfigType } from '@plone/registry';
import installSettings from './config/settings';

import './theme';

// Slate plugins
import installElementRenderer from './editor/renderer';
import installAlignLeftPlugin from './editor/plugins/AlignLeft';
import { ALIGNLEFT } from './editor/plugins/AlignLeft/constants';
import installAlignCenterPlugin from './editor/plugins/AlignCenter';
import { ALIGNCENTER } from './editor/plugins/AlignCenter/constants';
import installAlignRightPlugin from './editor/plugins/AlignRight';
import { ALIGNRIGHT } from './editor/plugins/AlignRight/constants';
import addStyleMenuFont from './editor/plugins/Font';
import addStyleMenuFontSize from './editor/plugins/FontSize';
import addStyleMenuFontWeight from './editor/plugins/FontWeight';
import addStyleMenuTextColors from './editor/plugins/TextColors';


// blocks
import AccordionBlock from './components/Blocks/Accordion';
import CallToActionBlock from './components/Blocks/CallToAction';
import CardsBlock from './components/Blocks/Cards';
import ContactEmailBlock from './components/Blocks/ContactEmail';
import DividerBlock from './components/Blocks/Divider';
import FAQsBlock from './components/Blocks/FAQs';
import FeaturesBlock from './components/Blocks/Features';
import HeroBlock from './components/Blocks/Hero';
import ItemsBlock from './components/Blocks/Items';
import StatisticsBlock from './components/Blocks/Statistics';
import TestimonialsBlock from './components/Blocks/Testimonials';

// listing variations
import { enhancedListingSchema } from './components/Blocks/schema';
import CardListingBlockTemplate from './components/Blocks/Listing/CardTemplate';
import NewsCardListingBlockTemplate from './components/Blocks/Listing/NewsCardTemplate';

// app extras
import GlobalLoader from './components/GlobalLoader/GlobalLoader';


function applyConfig(config: ConfigType) {
  installSettings(config);

  // General settings
  config.settings.navDepth = 5;

  // Install component that overrides render logic to apply classes to certain elements
  config = installElementRenderer(config);

  // Install additional Slate (WYSIWYG) buttons
  const slate: any = config.settings.slate;
  slate.toolbarButtons = [...(slate.toolbarButtons || []), ALIGNLEFT];
  config = installAlignLeftPlugin(config);

  slate.toolbarButtons = [...(slate.toolbarButtons || []), ALIGNCENTER];
  config = installAlignCenterPlugin(config);

  slate.toolbarButtons = [...(slate.toolbarButtons || []), ALIGNRIGHT];
  config = installAlignRightPlugin(config);

  config = addStyleMenuFont(config);
  config = addStyleMenuFontSize(config);
  config = addStyleMenuFontWeight(config);
  config = addStyleMenuTextColors(config);

  // Register blocks
  const blocksConfig = config.blocks.blocksConfig as Record<string, any>;
  blocksConfig.accordion = AccordionBlock;
  blocksConfig.call_to_action = CallToActionBlock;
  blocksConfig.cards = CardsBlock;
  // TODO: disabled till we know what to do with it
  // blocksConfig.contact_email = ContactEmailBlock;
  blocksConfig.dividers = DividerBlock;
  blocksConfig.faqs = FAQsBlock;
  blocksConfig.features = FeaturesBlock;
  blocksConfig.heroes = HeroBlock;
  blocksConfig.items = ItemsBlock;
  blocksConfig.statistics = StatisticsBlock;
  blocksConfig.testimonials = TestimonialsBlock;

  // Register block groups
  config.blocks.groupBlocksOrder = [
    ...(config.blocks.groupBlocksOrder || []),
    { id: 'castle', title: 'Castle.cms' },
  ];

  // Register blocks that will ONLY appear as options within the grid block
  // config.blocks.blocksConfig.gridBlock.blocksConfig['card'] = CardsBlock;
  // Enable blocks within gridblock
  const blockIds: string[] = [];
  const addBlocksToGrid = (blocksConfig: any, blockIds: string[]) => {
    const addBlocks = (target: any, ids: string[]) => {
      if (target?.allowedBlocks) {
        target.allowedBlocks = [...new Set([
          ...(target.allowedBlocks || []),
          ...ids
        ])];
      }
    };
    // the gridBlock object has two separate 'allowedBlocks' arrays
    // custom block id's must be pushed to both in order for them to appear
    addBlocks(blocksConfig.gridBlock, blockIds);
    addBlocks(blocksConfig.gridBlock?.blocksConfig?.gridBlock, blockIds);
  };
  addBlocksToGrid(blocksConfig, blockIds);

  // Add content listing display variations and enhanced schema
  const listing = blocksConfig.listing;
  listing.schemaEnhancer = enhancedListingSchema;
  listing.variations = [
    ...(listing.variations || []),
    {
      id: 'card-default',
      title: 'Card',
      template: CardListingBlockTemplate,
      isDefault: false,
    },
    {
      id: 'card-news',
      title: 'News Card',
      template: NewsCardListingBlockTemplate,
      isDefault: false,
    },
  ];

  // Register app extras
  const appExtras = config.settings.appExtras ?? [];
  config.settings.appExtras = [
    ...appExtras,
    {
      match: "",
      component: GlobalLoader,
      props: {},
    },
  ];

  return config;
}

export default applyConfig;

